/**
 * The desktop-app deep-link scheme + path helpers — the SINGLE source of truth
 * shared by every surface that emits or parses one:
 *   - the web SPA "Copy link" / "Share via email" builders
 *     (`apps/web/src/components/share/appShareLinks.ts` re-exports these);
 *   - the dashboard server's notification / share emails
 *     (`apps/server/src/services/channels/email.ts`), so a clicked link opens
 *     the DESKTOP APP, never the web version;
 *   - the Tauri shell, which registers + parses the same scheme in Rust
 *     (`apps/desktop/src-tauri/src/lib.rs` `DEEP_LINK_SCHEME`), declared in
 *     `tauri.conf.json` (`plugins.deep-link`) and macOS `Info.plist`
 *     (`CFBundleURLSchemes`).
 *
 * Keep the scheme string here in lockstep with those Rust / tauri.conf / plist
 * copies (they can't import TypeScript). Pure string logic — no I/O, no env.
 */

/** Custom URL scheme the installed desktop app is registered to handle. The OS
 * hands a clicked `xopstaff://…` link to the app, which navigates to the in-app
 * route — so the link opens the desktop app instead of the browser. */
export const DEEP_LINK_SCHEME = "xopstaff";

/**
 * Desktop deep-link for an in-app route PATH, e.g. `/crm/prospects/385` →
 * `xopstaff://crm/prospects/385` (a query string is preserved:
 * `/chat?dm=5` → `xopstaff://chat?dm=5`). The leading slash is dropped so the
 * route's first segment isn't swallowed as a URL host by lenient parsers;
 * {@link parseDeepLinkRoute} reverses it. The empty/`"/"` path yields the bare
 * `xopstaff://` (the app's home).
 */
export function appDeepLinkUrl(path: string): string {
  const clean = path.replace(/^\/+/, "");
  return `${DEEP_LINK_SCHEME}://${clean}`;
}

/**
 * Public **https** "open the app" bounce URL on the support portal.
 *
 * A custom-scheme link (`xopstaff://…`) is NOT clickable in a webmail client —
 * Gmail's web UI in particular renders it as inert text, so an email CTA that
 * points straight at the deep link is dead on arrival. Instead the CTA points
 * at a real https page the PUBLIC support portal serves —
 * `${base}/app/open?to=<in-app path>` — which reads `to` and hands off to
 * {@link appDeepLinkUrl} to open the desktop app (with a visible fallback for
 * anyone without it). The support portal is the only public web surface; the
 * dashboard itself has no public web access.
 *
 * `base` is the support portal's public web origin (edge-staff
 * `SUPPORT_CENTER_WEB_URL`, e.g. `https://support.xopnetworks.com`); `path` is an
 * in-app route (`/support/tickets/5`, or `/` for the app home). The `to` value
 * is URL-encoded so a query string in the path survives the round-trip.
 */
export function supportAppOpenUrl(base: string, path: string): string {
  const origin = base.replace(/\/+$/, "");
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${origin}/app/open?to=${encodeURIComponent(clean)}`;
}

/**
 * Sentinel deep-link path meaning "you opened me to update" — NOT a real SPA
 * route. The desktop-release email's CTA points here
 * (`services/desktopReleaseMailer.ts`), so clicking "Open XOP Command Center" runs a
 * VISIBLE update check on arrival instead of silently landing on the dashboard.
 *
 * Why it exists: the desktop app is single-instance + close-to-tray, so it is
 * usually ALREADY RUNNING when the link is clicked. The shell shows the window
 * and emits `xop://open-route`, but React never remounts — so the mount-time
 * update check never re-runs and the "vX is available" modal never appears
 * (2026-08-07 bug: "I have to click Check for Updates again"). The intent has
 * to travel in the link itself.
 *
 * The web side intercepts it (`useDesktopUpdate` runs the check,
 * `useDeepLinkRouting` declines to navigate) and lands the user on the app
 * home instead — never on this path.
 */
export const DEEP_LINK_UPDATE_PATH = "/check-update";

/** True when a parsed deep-link path is the {@link DEEP_LINK_UPDATE_PATH}
 * sentinel. Tolerant of a trailing slash and of case, since the value makes a
 * round trip through an email client, an https bounce page, and the OS. */
export function isUpdateCheckPath(path: string | null | undefined): boolean {
  if (!path) return false;
  const clean = path.trim().toLowerCase().replace(/\/+$/, "");
  return clean === DEEP_LINK_UPDATE_PATH;
}

/** Reverse of {@link appDeepLinkUrl}: turn an incoming `xopstaff://…` URL back
 * into an in-app route PATH (leading slash restored), or `null` if it isn't one
 * of ours. Tolerant of the extra/trailing slashes the OS or argv add
 * (`xopstaff:///crm/prospects/385`, `xopstaff://crm/prospects/385/`). */
export function parseDeepLinkRoute(url: string): string | null {
  const prefix = `${DEEP_LINK_SCHEME}://`;
  if (!url.startsWith(prefix)) return null;
  const rest = url.slice(prefix.length).replace(/^\/+/, "").replace(/\/+$/, "");
  return rest ? `/${rest}` : "/";
}
